"use client";

import { liteClient as algoliasearch } from "algoliasearch/lite";
import { useMemo, type ReactNode } from "react";
import { Configure, InstantSearch } from "react-instantsearch";

import type { Locale } from "@/domain/catalog";
import {
	buildListingFilters,
	type SuperCategory
} from "@/domain/product-listing";

const HITS_PER_PAGE = 24;

export interface ProductListingSearchCredentials {
	appId: string;
	apiKey: string;
	indexName: string;
}

type SearchClient = React.ComponentProps<typeof InstantSearch>["searchClient"];

export interface ProductListingResultsProps {
	category: SuperCategory;
	locale: Locale;
	search: ProductListingSearchCredentials;
	searchClient?: SearchClient;
	children: ReactNode;
}

export function ProductListingResults({
	category,
	locale,
	search,
	searchClient: searchClientOverride,
	children
}: ProductListingResultsProps): JSX.Element {
	const searchClient = useMemo(
		() =>
			searchClientOverride ?? algoliasearch(search.appId, search.apiKey),
		[searchClientOverride, search.appId, search.apiKey]
	);

	const filters = useMemo(
		() => buildListingFilters({ categoryName: category.name }),
		[category.name]
	);

	return (
		<InstantSearch
			searchClient={searchClient}
			indexName={search.indexName}
			future={{ preserveSharedStateOnUnmount: true }}
		>
			<Configure filters={filters} hitsPerPage={HITS_PER_PAGE} />
			{children}
		</InstantSearch>
	);
}
